<script setup lang="ts">
import * as Sentry from '@sentry/nuxt'

import InfluencerProfile from '~/components/influencer/Profile.vue'

import { useCartSnackbars } from '~/composables/useCartSnackbars'
import { useGetCurrentFullUrl } from '~/composables/useGetCurrentFullUrl'
import { useIntercomMove } from '~/composables/useIntercomMove'
import { useProgressivePromosCheck } from '~/composables/useProgressivePromosCheck'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'
import { useSegmentTrackCartAdditions } from '~/composables/useSegmentTrackCartAdditions'

import { useCartStore } from '~/stores/cart'
import { useDraftStore } from '~/stores/draft'
import { useInfluencersStore } from '~/stores/influencers'
import { useInfluencersRecommendationsStore } from '~/stores/influencersRecommendations'
import { useTagStore } from '~/stores/tag'
import { useUserStore } from '~/stores/user'
import { useUserBandStore } from '~/stores/userBand'
import { useUserFavoritesStore } from '~/stores/userFavorites'
import { useUserInfluencerStore } from '~/stores/userInfluencer'

import pictureBuildHelper from '~/helpers/images/picture_build'

import { isString } from '~/utils/type-guards'

import { provideFetchBandCuratorHistory } from '~/api-core/Band/BandCuratorHistory'
import { provideGetCuratorBySlug } from '~/api-core/Curators/GetCurator'
import { provideFetchProfileRecommendedSimilarInfluencers } from '~/api-core/Recommendation/FetchRecommendation'

import type { Influencer } from '~/types/influencer'
import type InfluencerInteraction from '~/types/influencerInteraction'

export interface _AsyncDataSharedSlug {
  influencer: Influencer
  influencerPicture: string
  suggested: Influencer['id'][]
}

definePageMeta({
  middleware: async (to) => {
    const { $pinia } = useNuxtApp()
    const userStore = useUserStore($pinia)
    const draftStore = useDraftStore($pinia)

    const {
      query: { draftId: draftIdParam },
    } = to

    // avoids infinity loops
    if (!userStore.IS_BAND || !draftIdParam) return

    const draftId = Number(draftIdParam)

    if (draftStore.id !== draftId) {
      const draft = await draftStore.FETCH_BY_ID(draftId).catch((err) => {
        Sentry.captureException(err)
      })

      if (!draft) return

      draftStore.SET(draft)
      // get the first batch of draft infs, but not all of them so that pages can load
      await draftStore.GET_CURRENT_DRAFT_INFLUENCERS()
      // continue to get the rest of the influencers if there are any without blocking the page
      draftStore.GET_CURRENT_DRAFT_INFLUENCERS({ fetchAll: true })
    }

    if (draftId) await draftStore.FETCH_MISSING_INFLUENCERS()
  },
})

const { params, query } = useRoute()
const { t, locale: LANG } = useI18n()
const {
  public: { ENV_NAME },
} = useRuntimeConfig()
const { coreFetch } = useProvideCoreFetch()
const { getCurrentFullUrl } = useGetCurrentFullUrl()

const { FETCH: FETCH_TAGS, GET_TAGS_FROM_IDS, GET_TAG_FROM_ID } = useTagStore()
const { FETCH_SET } = useInfluencersStore()
const { FETCH_DRAFT_CART } = useCartStore()

const { authorized: curatorIsAuthorized } = storeToRefs(
  useUserInfluencerStore(),
)
const { FETCH_BUCKETS, FETCH_INFLUENCERS } = useUserFavoritesStore()
const { id: BAND_ID } = storeToRefs(useUserBandStore())
const { id: DRAFT_ID } = storeToRefs(useDraftStore())
const { IS_INF, IS_BAND, IS_LOGGED_IN } = storeToRefs(useUserStore())
const { FETCH_RECOMMENDATION } = useInfluencersRecommendationsStore()
const { id: CART_ID } = storeToRefs(useCartStore())

const isRouterGoingToPreviousPage = ref<boolean>(false)

useSegmentTrackCartAdditions()
useCartSnackbars()
// move the intercom button above the campaign footer
useIntercomMove((SET_INTERCOM_HIGHER) => {
  if (!IS_INF.value) SET_INTERCOM_HIGHER()
})

onBeforeRouteLeave((_to, _from, next) => {
  isRouterGoingToPreviousPage.value = true
  next()
})

// First, we fetch the influencer data by slug
const { data: influencer } = await useAsyncData(
  'influencerRef',
  () => {
    const getCuratorBySlug = provideGetCuratorBySlug(coreFetch)
    return getCuratorBySlug(params.slug as string).catch(() => {
      return {} as Influencer
    })
  },
  {
    default: () => ({}) as Influencer,
  },
)

// If the influencer is not found, we throw a 404 error
if (!influencer.value || !influencer.value.id) {
  throw createError({
    message: 'Influencer not found',
    statusCode: 404,
  })
}

// Now that we have the influencer data, we can fetch the rest of the data for the page
// Kick off "not-awaited" data fetching (NOTE: this still blocks SSR loading)
Promise.all([
  callOnce('FETCH_TAGS', FETCH_TAGS),
  callOnce(
    `FETCH_INF_SET_${influencer.value.id}`,
    () => {
      FETCH_SET([influencer.value.id])
    },
    { mode: 'navigation' },
  ),
  callOnce('FETCH_RECOMMENDATIONS', FETCH_RECOMMENDATION, {
    mode: 'navigation',
  }),
])

// Kick off awaited data fetching
const [{ data: interactions }, { data: suggested }] = await Promise.all([
  await useLazyAsyncData<InfluencerInteraction[]>(
    'influencerInteractions',
    () => {
      if (!IS_LOGGED_IN.value || IS_INF.value) return Promise.resolve([])

      const getBandCuratorHistory = provideFetchBandCuratorHistory(coreFetch)
      return getBandCuratorHistory(influencer.value.id).catch(() => {
        return [] as InfluencerInteraction[]
      })
    },
    {
      default: () => [],
    },
  ),
  await useLazyAsyncData<Influencer['id'][]>(
    'suggestedInfluencers',
    async () => {
      const getProfileSimilarInfluencers =
        provideFetchProfileRecommendedSimilarInfluencers(coreFetch)
      const ids = getProfileSimilarInfluencers(influencer.value.id, {
        bandId: BAND_ID.value || undefined,
        draftId: DRAFT_ID.value || undefined,
      })
      return ids
    },
    { default: () => [] },
  ),
  await callOnce(
    'FETCH_INFLUENCERS',
    () => FETCH_INFLUENCERS({ fetchRecommended: false }),
    { mode: 'navigation' },
  ),
])

if (suggested.value.length) {
  await callOnce(
    'suggestedInfsSet',
    async () => await FETCH_SET(suggested.value),
    { mode: 'navigation' },
  )
}

// Setup head data
const {
  entity: influencerName,
  tags: {
    identity: { influencer_kind: influencerKindTagIds },
  },
} = influencer.value
const influencerKindTags = GET_TAGS_FROM_IDS(influencerKindTagIds)

const translatedInfluencerKinds = influencerKindTags.map(
  ({ name }) => t(`tags.influencer_kind.${name}`) as string,
)
// We want a maximum of 50 characters in order for titles to display correctly in Google
const maximumTitleLength = 50
let remainingSpace =
  maximumTitleLength -
  (influencerName.length + (t('shared.onGroover') as string).length)
const trimmedKinds = translatedInfluencerKinds.reduce(
  (acc, kind, index, array) => {
    remainingSpace -=
      kind.length + (index === array.length - 1 ? ' '.length : ', '.length)
    if (remainingSpace >= 0) acc.push(kind.toLowerCase())

    return acc
  },
  [] as string[],
)

const influencerCountry: string | undefined = GET_TAG_FROM_ID(
  influencer.value.tags?.identity.country[0] ?? 0,
)?.name

const title = t('metaTags.influencerProfileSlug.title', {
  influencerName,
  space: trimmedKinds.length ? ' ' : '',
  kinds: trimmedKinds.join(', '),
})

const description = t('metaTags.influencerProfileSlug.description', {
  influencerName,
})

const influencerPicture = pictureBuildHelper({
  kind: 'influencer',
  slug: influencer.value.has_profile_picture
    ? influencer.value.slug
    : undefined,
  size: '200_200',
  target: 'profile_picture',
})

/**
 * This is to avoid duplicate indexing on google
 */
const slugIsNotId = isString(params.slug) && params.slug.match(/\D/gi)

useHead({
  title,
  meta: [
    {
      name: 'description',
      key: 'description',
      content: description,
    },
    {
      key: 'og:title',
      property: 'og:title',
      content: title,
    },
    {
      key: 'og:description',
      property: 'og:description',
      content: description,
    },
    {
      property: 'og:image',
      key: 'og:image',
      content: influencerPicture,
    },
    {
      key: 'robots',
      name: 'robots',
      content:
        slugIsNotId &&
        ENV_NAME === 'prod' &&
        influencer.value.authorized &&
        !influencer.value.is_scout &&
        !influencer.value.disable_indexing
          ? 'index, follow'
          : 'noindex, follow',
    },
  ],
  link: [
    {
      rel: 'canonical',
      href: getCurrentFullUrl(),
    },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Corporation',
        name: influencerName,
        description:
          influencer.value[`description_${LANG.value as 'en' | 'fr'}` as const],
        url: `https://www.groover.co/influencer/profile/${influencer.value.slug}/`,
        address: influencerCountry
          ? {
              '@type': 'PostalAddress',
              addressCountry: t(`tags.country.${influencerCountry}`),
            }
          : undefined,
        logo: influencerPicture,
      }),
    },
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'ProfilePage',
        name: influencerName,
      }),
    },
  ],
})

const computedDraftId = computed<number>(() => Number(query.draftId ?? 0))

const route = useRoute()
const localePath = useLocalePath()
const shouldDisplayEditConfirmation = ref(false)

onMounted(async () => {
  if (IS_BAND.value) {
    const { draftId } = query
    const draftIdNumber = Number(draftId)
    const shouldGetDraftCart =
      draftIdNumber && !isNaN(draftIdNumber) && !CART_ID.value

    Promise.all([
      useProgressivePromosCheck(),
      FETCH_BUCKETS(),
      shouldGetDraftCart ? FETCH_DRAFT_CART(draftIdNumber) : Promise.resolve(),
    ]).catch(() => {
      // Do nothing
    })
  }

  if (route.query.confirm) {
    shouldDisplayEditConfirmation.value = true
    await navigateTo(localePath({ replace: true, query: {} }))
    window.setTimeout(() => {
      shouldDisplayEditConfirmation.value = false
    }, 3300)
  }
})
</script>

<template>
  <div
    id="ignoreFontUpscale"
    class="influencerProfileSlugWrapper ignoreFontUpscale tw-relative tw-w-full"
  >
    <Transition name="fade" mode="out-in">
      <LazyInfluencerProfileEditConfirm
        v-if="shouldDisplayEditConfirmation"
        hydrate-never
      />
    </Transition>

    <div v-if="!curatorIsAuthorized" class="notAuthorized">
      <LazyNavConfirmationsInfluencerValidation v-if="!curatorIsAuthorized" />
      <LazyNavConfirmationsInfluencerSignup v-if="!curatorIsAuthorized" />
    </div>
    <InfluencerProfile
      v-if="!isRouterGoingToPreviousPage"
      :influencer="influencer"
      :interactions="interactions"
      :suggested="suggested"
    />
    <!-- ClientOnly used to keep dynamic content out of cdn cache -->
    <ClientOnly>
      <LazyCampaignFooter
        v-if="!IS_INF"
        page="profile"
        :influencer="influencer"
        :draft-id="computedDraftId"
      />
      <template v-if="!IS_INF" #fallback>
        <div
          class="tw-fixed tw-bottom-0 tw-left-0 tw-h-[83px] tw-w-full tw-bg-white tw-shadow-[shadow:var(--shadow-regular-dark)]"
        ></div>
      </template>
    </ClientOnly>
  </div>
</template>
